import React, { useCallback, useEffect, useState } from "react";
import CommonModal from "../../../../components/modal/CommonModal";
import InputText from "../../../../components/inputs/InputText";
import { searchPurchaseProducts } from "../../../../api/inventoryService";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import {
  Pack,
  derivePurchaseLine,
  resolveDefaultPack,
  unitsForPack,
} from "../../inventoryPackUtils";

interface Product {
  productId: string;
  productName: string;
  scale: string;
  packName: string | null;
  packQuantity: number;
  unitsPerPack: number;
  packPrice: number;
  quantity: number;
  unitPrice: number;
  price: number;
  paid: Boolean;
  errors: Boolean;
}

interface Item {
  [key: string]: any;
}

interface AddProductModalProps {
  closeModal: () => void;
  addProduct: (product: Product) => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  closeModal,
  addProduct,
}) => {
  const [formData, setFormData] = useState({
    productId: "",
    productName: "",
    scale: "",
    packName: "",
    packQuantity: 1 as number | string,
    unitsPerPack: 1 as number | string,
    packPrice: 0 as number | string,
    errors: false,
  });

  const [product, setProduct] = useState<Item[]>([]);
  const [packs, setPacks] = useState<Pack[]>([]);

  const unit = formData.scale || "unit";
  const packQuantity = Number(formData.packQuantity) || 0;
  const unitsPerPack = Number(formData.unitsPerPack) || 0;
  const packPrice = Number(formData.packPrice) || 0;

  const { quantity, price, unitPrice } = derivePurchaseLine(
    packQuantity,
    unitsPerPack,
    packPrice
  );

  const getProductData = useCallback(async (search: string) => {
    try {
      const data = await searchPurchaseProducts(search);
      setProduct(data as Item[]);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getProductData("");
  }, [getProductData]);

  const searchProduct = useCallback(
    (search: string) => {
      getProductData(search);
    },
    [getProductData]
  );

  const handleChange = useCallback(
    (e: any) => {
      const { name, value, valueTitle } = e.target;

      if (name === "productId") {
        const selected = product.find((item) => item?._id === value);
        // Narrow off a local, not through the optional chain: TypeScript
        // does not carry the isArray guard across `selected?.packs` twice.
        const selectedPacks = selected?.packs;
        const nextPacks: Pack[] = Array.isArray(selectedPacks)
          ? selectedPacks
          : [];
        const preselected = resolveDefaultPack(nextPacks);
        setPacks(nextPacks);
        setFormData((prevData) => ({
          ...prevData,
          productId: value,
          productName: valueTitle,
          scale: selected?.scale ?? "",
          packName: preselected ? preselected.name : "",
          unitsPerPack: preselected ? Number(preselected.unitsPerPack) : 1,
        }));
        return;
      }

      setFormData((prevData) => ({ ...prevData, [name]: value }));
    },
    [product]
  );

  const handlePackChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const name = e.target.value;
      setFormData((prevData) => ({
        ...prevData,
        packName: name,
        unitsPerPack: unitsForPack(packs, name),
      }));
    },
    [packs]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (
        formData.productId === "" ||
        formData.productName === "" ||
        packQuantity <= 0 ||
        unitsPerPack < 1 ||
        price <= 0
      ) {
        setFormData((prevData) => ({ ...prevData, errors: true }));
        return;
      }

      addProduct({
        productId: formData.productId,
        productName: formData.productName,
        scale: formData.scale,
        packName: formData.packName || null,
        packQuantity,
        unitsPerPack,
        packPrice,
        quantity,
        unitPrice,
        price,
        paid: false,
        errors: false,
      });
    },
    [
      addProduct,
      formData,
      packQuantity,
      unitsPerPack,
      packPrice,
      quantity,
      unitPrice,
      price,
    ]
  );

  const packLabel = formData.packName || unit;

  return (
    <CommonModal closeModal={closeModal} title={"Add Product"}>
      <form onSubmit={handleSubmit}>
        <InputDropSearch
          data={product}
          name="productId"
          required
          label="Product Name"
          selectName1="productName"
          selectValue="_id"
          value2Name="scale"
          onChange={handleChange}
          onSearch={searchProduct}
          errors={formData.errors && formData.productId === ""}
        />

        <div className="w-full flex flex-col gap-1.5 my-1.5">
          <label className="block text-sm font-normal text-slate-700 dark:text-slate-300">
            Buying As
          </label>
          <select
            value={formData.packName}
            onChange={handlePackChange}
            disabled={formData.productId === ""}
            className="w-full h-10 px-3 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <option value="">{unit} (base unit)</option>
            {packs.map((pack, i) => (
              <option key={i} value={pack.name}>
                {pack.name} ({pack.unitsPerPack} {unit})
              </option>
            ))}
          </select>
        </div>

        <InputText
          name="packQuantity"
          required
          label={`Number Of ${packLabel}`}
          value={formData.packQuantity}
          onChange={handleChange}
          type="number"
          className="form__label__none"
          errormessage={`Number of ${packLabel} must be greater than zero.`}
          errors={formData.errors && packQuantity <= 0}
        />

        {formData.packName !== "" && (
          <InputText
            name="unitsPerPack"
            required
            label={`${unit} Per ${formData.packName}`}
            value={formData.unitsPerPack}
            onChange={handleChange}
            type="number"
            className="form__label__none"
            errormessage={"Must be at least 1."}
            errors={formData.errors && unitsPerPack < 1}
          />
        )}

        <InputText
          name="packPrice"
          required
          label={`Price Per ${packLabel}`}
          value={formData.packPrice}
          onChange={handleChange}
          type="number"
          className="form__label__none"
          errormessage={"Price must be greater than zero."}
          errors={formData.errors && price <= 0}
        />

        {quantity > 0 && (
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/40 rounded-lg my-1.5 space-y-0.5">
            <p className="text-xs text-slate-700 dark:text-slate-200">
              {packQuantity} {packLabel}
              {formData.packName !== "" && (
                <>
                  {" "}
                  &times; {unitsPerPack} {unit} ={" "}
                  <span className="font-bold text-indigo-600 dark:text-indigo-300">
                    {quantity} {unit}
                  </span>
                </>
              )}
            </p>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Total {price.toFixed(2)}
              {quantity > 0 && (
                <>
                  {" "}
                  &middot; {unitPrice.toFixed(4)} per {unit}
                </>
              )}
            </p>
          </div>
        )}

        <button type="submit">submit</button>
      </form>
    </CommonModal>
  );
};

export default AddProductModal;
